import cookieParser from 'cookie';
import { NextPageContext } from 'next';

import { DoRequest } from '@r1-frontend/do-request/core/DoRequest';
import getCityInfo, { ResponseDto as CityInfoResponseDto } from '@r1-frontend/api-domru/api-content/v1/geography/get-city-info';
import { ResponseDto as MenuResponseDto } from '@r1-frontend/api-domru/api-content/v2/menu/by-types';
import menuByVariantAliases from '@r1-frontend/api-domru/api-content/v2/menu/by-variant-aliases';
import { ICurrentCity } from '@r1-frontend/entities/City/models/ICurrentCity';
import { formatterMenuDataByResponse } from '@r1-frontend/entities/stores/city/actions/menu';
import { INewMenuItems } from '@r1-frontend/entities/stores/city/reducer';

import { CITYDOMAIN_COOKIE } from '@r1-frontend/shared/constants';
import Cookies from '@r1-frontend/shared/helpers/Cookies';
import { generateAbtTestValue } from '@r1-frontend/shared/helpers/generateAbtTestValue';
import { IRequest } from '@r1-frontend/shared/types/IRequest';

import CurrentCity from '~/src/app/city/CurrentCity';
import {
    ABN_TEST_COOKIE,
    ABN_VARIANTS_COOKIE,
    COOKIE_MAX_AGE_LONG,
    COOKIE_MAX_AGE_SHORT,
    PROVIDER_ID_COOKIE,
} from '~/src/constants';
import { REDESIGN_MENU_UNAUTH } from '~/src/features/index/constants/abnVariants';
import { getABNVariantFrom, getABNVariants } from '~/src/helpers/abn-tests';
import getRequester from '~/src/helpers/getRequester';

const LONG_COOKIES_CONFIG = { maxAge: COOKIE_MAX_AGE_LONG };
const SHORT_COOKIES_CONFIG = { maxAge: COOKIE_MAX_AGE_SHORT };
const SEC_IN_YEAR = 31536000;

class InitialCity {
    private readonly doRequest: DoRequest<IRequest>;
    private readonly cookies: Cookies;

    constructor(private readonly ctx: NextPageContext) {
        if (!ctx || !ctx.req || !ctx.res) {
            throw new Error('[baseInitialProps] InitialCity could not be initialized without Next Context');
        }

        this.doRequest = getRequester(ctx.req);
        this.cookies = new Cookies(ctx);
    }

    public async getAll(
    ): Promise<{ providerId: number, currentCity: ICurrentCity | Omit<ICurrentCity, 'abnTests'>, abnVariants: string, menus: INewMenuItems }> {
        const cityInfo = await this.getCityInfo();
        const providerId = cityInfo.provider_id;

        const currentCity = this.getCurrentCity(cityInfo);
        const abnVariants = this.getAbnVariants(cityInfo);
        const menus = await this.getMenus(providerId, abnVariants);

        this.logAll();

        return {
            providerId,
            currentCity,
            abnVariants,
            menus,
        };
    }

    private async getCityInfo() {
        const cityFromHostOrCookie = this.ctx.query['city'] || this.cookies.get(CITYDOMAIN_COOKIE);
        const domain = Array.isArray(cityFromHostOrCookie)
            ? cityFromHostOrCookie[0]
            : cityFromHostOrCookie;


        const providerIdCookie = this.cookies.get(PROVIDER_ID_COOKIE);
        const providerId = providerIdCookie ? Number(providerIdCookie) : 0;

        // todo: Добавить in memory cache
        const response = await this.doRequest.apiContent<CityInfoResponseDto>(
            getCityInfo({
                expand: 'promolines',
                /**
                 * Для ситуации когда у нас в одном городе несколько провайдеров в не АЗ мы всегда отображаем активного провайдера,
                 * а в АЗ должен отображаться текущий город в зависимости от куки providerId.
                 */
                ...providerId ? { providerId, onlyActive: false } : { domain: domain ?? '' },
            }),
        );
        if (!response.isSuccess) {
            throw new Error('[baseInitialProps] Unknown current city. Api-content /v1/geography/get-city-info request failed!');
        }

        return response.payload;
    }

    private getCurrentCity(cityInfo: CityInfoResponseDto) {
        const currentCity = new CurrentCity(cityInfo);
        const nextCurrentCity = currentCity.prepare();

        this.cookies.set([
            { name: CITYDOMAIN_COOKIE, value: nextCurrentCity.domain, config: LONG_COOKIES_CONFIG },
            { name: PROVIDER_ID_COOKIE, value: cityInfo.provider_id, config: LONG_COOKIES_CONFIG },
        ]);

        return nextCurrentCity;
    }

    private getAbnVariants(cityInfo: CityInfoResponseDto): string {
        let abnTestValue = this.cookies.get(ABN_TEST_COOKIE);

        /**
         * У пользователей может быть сохранен в куках abnTestValue с 0.
         * Это приводит к неправильно обработанным вариантам, поэтому мы хотим их перегенерировать.
         * todo: Примерно осенью 2025 года можно будет удалить
         */
        if (abnTestValue && abnTestValue.endsWith('.0')) {
            this.cookies.delete(ABN_TEST_COOKIE);
            return this.getAbnVariants(cityInfo);
        }

        if (!abnTestValue) {
            abnTestValue = generateAbtTestValue();
        }
        const nextVariants = getABNVariants(cityInfo.abnTests, abnTestValue);

        this.cookies.set([
            { name: ABN_TEST_COOKIE, value: abnTestValue, config: { maxAge: SEC_IN_YEAR } },
            { name: ABN_VARIANTS_COOKIE, value: nextVariants, config: SHORT_COOKIES_CONFIG },
        ]);

        return nextVariants;
    }

    private async getMenus(providerId: number, abnVariants: string) {
        const abnTests = [
            REDESIGN_MENU_UNAUTH,
            /**
             * Add your abn-tests here ...
             */
        ];

        const abnVariantsAliases = abnTests.map(test => getABNVariantFrom(test.testName, abnVariants)).join(',');

        // todo: Добавить in memory cache
        const resp = await this.doRequest.apiContent<MenuResponseDto>(
            menuByVariantAliases({
                providerId,
                abnVariantsAliases,
            }),
        );

        if (!resp.isSuccess) {
            throw new Error('[baseInitialProps] Cannot receive menu data. Api-content /v2/menu/by-variant-aliases request failed!');
        }

        return formatterMenuDataByResponse(resp.payload);
    }

    private logAll() {
        if (!this.ctx.req) {
            return;
        }
        const userAgent = this.ctx.req.headers['user-agent'];

        const parsedCookie = cookieParser.parse(this.ctx.req.headers.cookie ?? '');
        const analyticsId = parsedCookie['_ym_uid'] || 'none';
        const abnTestPersonalId = parsedCookie[ABN_TEST_COOKIE];
        const cookieAbn = parsedCookie[ABN_VARIANTS_COOKIE];
        const providerId = Number(parsedCookie[PROVIDER_ID_COOKIE]);

        console.log(JSON.stringify({
            event: 'InitialAppProps',
            fromUrl: this.ctx.req?.url || 'none',
            fromReferer: this.ctx.req?.headers.referer || 'none',

            userAgent,
            analyticsId,

            providerId,
            cookieAbn,
            abnTestPersonalId,
        }));
    }
}

export default InitialCity;
